import { useMemo } from 'react';
import { Mold, MoldStatus } from '../types/mold';
import { StatusFilterType } from '../components/MoldList/types';

export interface StatusCountConfig {
  // Function to determine if a mold matches this status
  matcher: (mold: Mold) => boolean;
  // For standard status filters, specify the mold status value(s)
  moldStatuses?: MoldStatus[];
}

// Define how each status filter maps to mold data
const STATUS_COUNT_DEFINITIONS: Record<StatusFilterType, StatusCountConfig> = {
  ALL: {
    matcher: () => true, // All molds match
  },
  ACTIVE: {
    moldStatuses: ['active'],
    matcher: (m) => m.status === 'active',
  },
  IN_SERVICING: {
    moldStatuses: ['in_servicing'],
    matcher: (m) => m.status === 'in_servicing',
  },
  INACTIVE: {
    moldStatuses: ['inactive', 'eol'],
    matcher: (m) => m.status === 'inactive' || m.status === 'eol',
  },
  ARRIVING: {
    moldStatuses: ['arriving'],
    matcher: (m) => m.status === 'arriving',
  },
  RELEASED_TO_EXTERNAL: {
    moldStatuses: ['released_to_external'],
    matcher: (m) => m.status === 'released_to_external',
  },
  // Custom filter - not based on a single status field
  ATTENTION_REQUIRED: {
    matcher: (m) => m.cycleShotCount >= m.servicingAlertShotThreshold && m.cycleShotCount > 0,
  },
};

/**
 * Hook to calculate status counts for molds
 * @param molds - Array of molds to count
 * @returns Record of status counts
 */
export const useStatusCounts = (molds: Mold[]): Record<StatusFilterType, number> => {
  return useMemo(() => {
    const counts: Partial<Record<StatusFilterType, number>> = {};

    // Calculate count for each status type
    (Object.keys(STATUS_COUNT_DEFINITIONS) as StatusFilterType[]).forEach((statusType) => {
      const config = STATUS_COUNT_DEFINITIONS[statusType];
      counts[statusType] = molds.filter(config.matcher).length;
    });

    return counts as Record<StatusFilterType, number>;
  }, [molds]);
};

/**
 * Get the matcher function for a specific status type
 * Useful for filtering molds by status
 */
export const getStatusMatcher = (statusType: StatusFilterType): ((mold: Mold) => boolean) => {
  return STATUS_COUNT_DEFINITIONS[statusType]?.matcher || (() => false);
};

/**
 * Get the mold status values associated with a status filter type
 * Returns undefined for custom filters like ATTENTION_REQUIRED
 */
export const getMoldStatusesForFilter = (statusType: StatusFilterType): MoldStatus[] | undefined => {
  return STATUS_COUNT_DEFINITIONS[statusType]?.moldStatuses;
};
